import ENV from "@/utils/env";
import { useGetContextPack, useUpdateContext, useUpdateContextPack } from "@/api/studio/contexts/queries";
import { StudiesLayout } from "@/components/layouts";
import { ContextCanvas } from "@/components/ui/canvas";
import { useState } from "react";
import { useDropzone } from "@/features/contexts/hooks/use-dropzone";
import { useSegment } from "@/hooks/use-segment";
import { ContextPackDropzoneSkeleton } from "@/features/contexts/components/context-pack-dropzone-skeleton";
import { Error } from "@/components/fallbacks/error-boundaries/error";
import { ContextDropzoneNavbar } from "@/features/contexts/components/context-dropzone-navbar";
import { AddContextDropzone } from "@/features/contexts/components/add-context-dropzone";
import { ContextDropzonePreview } from "@/features/contexts/components/context-dropzone-preview";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";

/* Prop Types */
interface StudyAssetContextPackDropzoneRouteProps {
  projectId: string;
  studyId: string;
  contextPackId: string;
}

/*
 * StudyAssetContextPackDropzoneRoute component
 *
 * This route allows the user to add a dropzone to each context in the context pack
 */
export const StudyAssetContextPackDropzoneRoute = ({ projectId, studyId, contextPackId }: StudyAssetContextPackDropzoneRouteProps) => {
  /* State */
  const [assetIndex, setAssetIndex] = useState(0);

  /* Queries */
  const { data: contextPack, isLoading: isContextPackLoading, isError: isContextPackError } = useGetContextPack(contextPackId);
  const { mutateAsync: updateContextPack, isPending: isUpdateContextPackPending } = useUpdateContextPack();
  const { mutateAsync: updateContextDropzone, isPending: isUpdateContextDropzonePending } = useUpdateContext();

  /* Hooks */
  const { points, setPoints, dropzoneBackground, setDropzoneBackground, setShowDropzoneBackground, showDropzoneBackground, resetDropzoneBackground, setDropzoneId } = useDropzone(
    contextPack,
    assetIndex,
  );
  const { segmentTrackEvent } = useSegment();
  const { navigateTo } = useStudyNavigation(projectId, studyId, "study-context");

  /* Consts */
  const isEmptyDropzone = contextPack?.contexts[assetIndex]?.dropzone?.points?.length === 0 && points.length === 0;

  /* Handlers */
  const handleNext = async (isPublish: boolean) => {
    const contextId = contextPack?.contexts[assetIndex].id;
    if (!contextId) return;
    // if it's the last context, publish the context pack otherwise go to the next context
    if (isPublish) {
      await updateContextDropzone({ contextId, contextPackId, values: { dropzone: { points, backgroundColor: dropzoneBackground } } });
      await updateContextPack({ contextPackId, values: { isPublished: isPublish } });

      // Segment event for publishing the context pack
      segmentTrackEvent("Context Pack Published", {
        contextPackId,
        name: contextPack?.name,
        contextCount: contextPack?.contextCount,
        origin: "Study",
      });

      // navigate user back to the study context page
      navigateTo(`/projects/${projectId}/studies/${studyId}/asset/study-context`);
    } else {
      // save the dropzone against the context
      const contextId = contextPack?.contexts[assetIndex].id;
      if (!contextId) return;
      await updateContextDropzone({ contextId, contextPackId, values: { dropzone: { points, backgroundColor: dropzoneBackground } } });

      // go to the next context
      setAssetIndex(assetIndex + 1);

      // Segment tracking
      segmentTrackEvent("Context Dropzone Configuration Navigated", {
        contextPackId,
        contextPackName: contextPack?.name,
        useCaseId: contextPack?.useCase.id,
        useCaseName: contextPack?.useCase.name,
        contextCount: contextPack?.contextCount,
        direction: "forwards",
      });
    }

    // reset state
    resetDropzoneBackground();
  };

  const handlePrevious = async () => {
    // go to the previous context
    setAssetIndex(assetIndex - 1);
    // reset state
    resetDropzoneBackground();

    // Segment tracking
    segmentTrackEvent("Context Dropzone Configuration Navigated", {
      contextPackId,
      contextPackName: contextPack?.name,
      useCaseId: contextPack?.useCase.id,
      useCaseName: contextPack?.useCase.name,
      contextCount: contextPack?.contextCount,
      direction: "backwards",
    });
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-context">
      {/* Loading state */}
      {isContextPackLoading && <ContextPackDropzoneSkeleton />}

      {/* Error state */}
      {isContextPackError && <Error />}

      {/* Dropzone */}
      {contextPack && contextPack.contexts[assetIndex] && contextPack.contexts.length && (
        <>
          <div className="flex justify-between items-center w-full">
            {/* Navbar */}
            <ContextDropzoneNavbar
              contextName={contextPack?.contexts[assetIndex]?.name}
              contextPack={contextPack}
              previous={handlePrevious}
              next={handleNext}
              assetIndex={assetIndex}
              nextDisabled={isEmptyDropzone || isUpdateContextDropzonePending}
              previousDisabled={isUpdateContextDropzonePending}
            />
          </div>
          <div className="grow flex flex-row bg-base-300 rounded-lg overflow-hidden max-h-full h-screen relative">
            {/* Add dropzone */}
            {isEmptyDropzone && (
              <AddContextDropzone contextPack={contextPack} isUpdateContextPackPending={isUpdateContextPackPending} setPoints={setPoints} assetIndex={assetIndex} />
            )}

            {/* Editor */}
            <ContextCanvas
              contextId={contextPack.contexts[assetIndex].id}
              imageUrl={`${ENV.API_PLATFORM_URL}/v3/contexts/${contextPack.contexts[assetIndex].id}/image?maxWidth=1024&maxHeight=1024`}
              assetSize={{ width: contextPack.contexts[assetIndex].width, height: contextPack.contexts[assetIndex].height }}
              dropzone={{ points, backgroundColor: dropzoneBackground }}
              className={`${isEmptyDropzone ? "pointer-events-none opacity-60" : ""}`}
              dropzoneBackground={dropzoneBackground}
              setDropzoneBackground={setDropzoneBackground}
              showDropzoneBackground={showDropzoneBackground}
              setShowDropzoneBackground={setShowDropzoneBackground}
              setDropzoneId={setDropzoneId}
              setPoints={setPoints}
            />
          </div>

          {/* Bottom context preview */}
          <div className="flex justify-center items-center w-full gap-4">
            <ContextDropzonePreview assetIndex={assetIndex} contextPack={contextPack} />
          </div>
        </>
      )}
    </StudiesLayout>
  );
};
